import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, ValidatorFn, Validators } from '@angular/forms';
import {
  CorporateHolderModel,
  IndividualHolderModel,
  SecurityRegistryRecord
} from '@models/shares/security-registry-record';
import { BeneficialOwnerComponent } from '@shared/components/shares/beneficial-owner/beneficial-owner.component';
import { ValidationErrorComponent } from '@shared/controls/validation-error/validation-error.component';
import { SelectShareholderComponent } from '../select-shareholder/select-shareholder.component';
import { customSignErrors } from '../../models/shares/company-security-cancellation.model';
import { groupSecurityRecordsByName } from '../../../shared/utils/security.helper';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { CompanySecurityTransfer } from "../../models/shares";

export interface ShareholderWithSignControls {
  securityRegistryRecordGroup: FormControl<SecurityRegistryRecord[] | null>;
}

export interface ISelectedSecurityGroupHoldersGroupedByName {
  key: string,
  value: (IndividualHolderModel | CorporateHolderModel)[],
  relationshipIds: string[]
}

@Component({
  selector: 'app-shareholder-with-nbo-sign',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    SelectShareholderComponent,
    BeneficialOwnerComponent,
    ValidationErrorComponent
  ],
  templateUrl: 'shareholder-with-nbo-sign.component.html',
  styleUrl: 'shareholder-with-nbo-sign.component.scss',
})
export class ShareholderWithNboSignComponent implements OnInit, OnChanges {
  destroyRef = inject(DestroyRef);

  @Input() formGroup: FormGroup<ShareholderWithSignControls> = ShareholderWithNboSignComponent.defineForm();
  @Input() securityRegistryRecords: SecurityRegistryRecord[] = [];
  @Input() isBeneficialOwnerReadonly = false;

  @Output() select = new EventEmitter<SecurityRegistryRecord[]>();

  groupedSecurityRegistryRecords: SecurityRegistryRecord[][] = [];
  selectedSecurityGroupHoldersGroupedByName: ISelectedSecurityGroupHoldersGroupedByName[] = [];
  isBeneficialOwner = false;

  ngOnInit(): void {
    this.setSignValidator();

    this.formGroup.controls.securityRegistryRecordGroup.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value: SecurityRegistryRecord[] | null) => {
        const record = (value?.flatMap(r => r.holders) ?? [])
          .reduce((acc, holder) => {
            const key = holder.name;

            if (acc[key]) {
              acc[key].push(holder);
            } else {
              acc[key] = [holder];
            }

            return acc;
          }, {} as Record<string, (IndividualHolderModel | CorporateHolderModel)[]>);

        this.selectedSecurityGroupHoldersGroupedByName = Object.entries(record).map(([key, value]) => ({
          key,
          value,
          relationshipIds: value.map(v => v.relationshipId)
        }));

        this.isBeneficialOwner = value?.every((record) => record.isBeneficialOwner) ?? false;
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['securityRegistryRecords']) {
      this.groupedSecurityRegistryRecords = groupSecurityRecordsByName(this.securityRegistryRecords);
    }
  }

  onSelectCurrentShareholder(record: SecurityRegistryRecord[]): void {
    this.select.emit(record);
  }

  signDataSaved(): void {
    this.formGroup.controls.securityRegistryRecordGroup.updateValueAndValidity();
  }

  setSignValidator(): void {
    this.formGroup.controls.securityRegistryRecordGroup.setValidators([((control: FormControl<SecurityRegistryRecord[] | null>) => {
      const group = control.value;
      if (group === null) {
        return null;
      }

      const everyNBOHoldersHaveSigns = group.flatMap(r => r.holders)
        .filter(holder => !holder.details.isBeneficialOwner)
        .every(holder => holder.details.beneficialOwner);

      return everyNBOHoldersHaveSigns ? null : { nonBeneficialOwnersNotSigned: true };
    }) as ValidatorFn]);
  }

  get beneficialOwnersSignsErrorMessageHidden(): boolean {
    return this.formGroup.touched && this.formGroup.controls.securityRegistryRecordGroup.errors?.['nonBeneficialOwnersNotSigned'] as boolean;
  }

  static defineForm(): FormGroup<ShareholderWithSignControls> {
    return new FormGroup({
      securityRegistryRecordGroup: new FormControl<SecurityRegistryRecord[] | null>(null, [Validators.required]),
    });
  }

  protected readonly customSignErrors = customSignErrors;

  setupChange(change: CompanySecurityTransfer) {
    const selectedSecurityRegistryGroup =
      this.groupedSecurityRegistryRecords.find((group) => group.some((record) => record?.holdings.some((holder) => holder.securityHoldingId === change.transferFrom.securityHoldingId)));

    if (selectedSecurityRegistryGroup) {
      this.formGroup.controls.securityRegistryRecordGroup.setValue(selectedSecurityRegistryGroup ?? null);
    }
  }

  get includesNonBeneficialOwnersNotSignedError(): boolean {
    return this.formGroup.controls.securityRegistryRecordGroup.touched
      && this.formGroup.controls.securityRegistryRecordGroup.errors
      && this.formGroup.controls.securityRegistryRecordGroup.errors['nonBeneficialOwnersNotSigned'];
  }
}
